import React, { useEffect, useState } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { FormControl, FormHelperText } from '@mui/material'

import { AxleAccount, AxlePolicy, DefaultElementProps } from 'common/interfaces'
import GenericModal from 'components/Modals/GenericModal'
import { SizingContainer } from 'components/SizingContainer'
import { axleExchange, axleIgnition, getCompanySlug, useFlowSlug } from 'utils/flow'
import { validationsToAxleIframeRules } from 'utils/validationsToRules'

import AxleExitStatus from './AxleExitStatus'
import ConnectedAccount from './ConnectedAccount'
import ConnectingToAxle from './ConnectingToAxle'
import { AxleIframe as AxleIframeProps } from './interface'

enum AxleIframeComponentStatus {
  START = 'start',
  CONNECT = 'connect',
  EXITED = 'exited',
  CONNECTED = 'connected',
}

export default function AxleIframe({
  name,
  validations,
}: AxleIframeProps & DefaultElementProps): JSX.Element {
  const { control, setError } = useFormContext()
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    rules: validationsToAxleIframeRules(validations),
  })

  const flowSlug = useFlowSlug()
  const companySlug = getCompanySlug()

  const [iFrameSrc, setIFrameSrc] = useState('')
  const [axleStatus, setAxleStatus] = useState<AxleIframeComponentStatus>(
    AxleIframeComponentStatus.START
  )
  const [account, setAccount] = useState<AxleAccount | null>(null)
  const [policy, setPolicy] = useState<AxlePolicy | null>(null)
  const [modalOpen, setModalOpen] = useState(false)

  // Helper function to add the query parameter
  const addQueryParam = (url: string, paramName: string, paramValue: string): string => {
    const urlObj = new URL(url)
    urlObj.searchParams.set(paramName, paramValue)
    return urlObj.toString()
  }

  useEffect(() => {
    setAxleStatus(
      field.value?.axleStatus ? field.value?.axleStatus : AxleIframeComponentStatus.START
    )
    setAccount(field.value?.account)
    setPolicy(field.value?.policy)
  }, [field.value?.axleStatus, field.value?.account, field.value?.policy])

  useEffect(() => {
    const fetchIgnitionUrl = async (): Promise<void> => {
      const sessionFlowIdLS = localStorage.getItem(`${flowSlug}/sessionFlowId`)

      const ignitionResponse = await axleIgnition(flowSlug, companySlug, name, sessionFlowIdLS)
      const finalUrl = addQueryParam(ignitionResponse.ignitionUri, 'origin', window.location.origin)
      setAxleStatus(AxleIframeComponentStatus.CONNECT)
      setIFrameSrc(finalUrl)
    }

    if (
      (field.value?.account && field.value?.policy) ||
      (field.value?.axleStatus !== AxleIframeComponentStatus.START &&
        field.value?.axleStatus !== AxleIframeComponentStatus.CONNECT) ||
      !field.value?.axleStatus
    ) {
      return
    }

    void fetchIgnitionUrl()
  }, [field.value?.axleStatus])

  // Handle message events from iframe
  useEffect(() => {
    if (!field.value?.axleStatus) {
      field.onChange({ ...field.value, axleStatus: AxleIframeComponentStatus.START })
    }

    const handleMessage = (event: MessageEvent): void => {
      if (iFrameSrc && event.origin !== new URL(iFrameSrc).origin) {
        console.warn('Origin not allowed:', event.origin)
        return
      }

      switch (event.data.status) {
        case 'complete':
          console.log('Received completed message from Axle Ignition...', event.data)
          void handleCompleteIgnition(event.data.authCode)
          break
        case 'exit':
          console.log('Received exited message from Axle Ignition...', event.data)
          field.onChange({
            ...field.value,
            axleStatus: AxleIframeComponentStatus.EXITED,
          })
          break
        case 'error':
          console.log('Received errored message from Axle Ignition...', event.data)
          alert('Error connecting to Axle, please try again.')
          setError(name, { message: 'Could not connect account, please try again' })
          restartComponentValue()
          break
        default:
          console.log('Received unknown message from Axle Ignition...')
          break
      }
    }

    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [])

  const handleCompleteIgnition = async (authCode: string): Promise<void> => {
    const exchangeResponse = await axleExchange(flowSlug, companySlug, name, authCode)
    if (exchangeResponse.account && exchangeResponse.policy) {
      field.onChange({
        ...field.value,
        account: exchangeResponse.account,
        policy: exchangeResponse.policy,
        axleStatus: AxleIframeComponentStatus.CONNECTED,
      })
    }
  }

  const restartComponentValue = (): void => {
    setAxleStatus(AxleIframeComponentStatus.START)
    field.onChange({
      axleStatus: AxleIframeComponentStatus.START,
      account: null,
      policy: null,
    })
    setIFrameSrc('')
  }

  const restartConnectedSession = (): void => {
    restartComponentValue()
    setModalOpen(false)
  }

  const confirmRestartSession = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
    event.preventDefault()
    setModalOpen(true)
  }

  const errorMessageId = `${name}-helper-text`

  return (
    <SizingContainer>
      <FormControl fullWidth error={error !== undefined}>
        {error !== undefined && (
          <FormHelperText id={errorMessageId} role="alert">
            {error.message}
          </FormHelperText>
        )}
        {axleStatus === AxleIframeComponentStatus.START && <ConnectingToAxle />}
        {axleStatus === AxleIframeComponentStatus.CONNECT && (
          <iframe
            src={iFrameSrc}
            className="ignition"
            style={{ width: '100%', border: 'none', height: '650px' }}
          ></iframe>
        )}
        {axleStatus === AxleIframeComponentStatus.CONNECTED && (
          <ConnectedAccount
            account={account}
            policy={policy}
            restartConnection={confirmRestartSession}
          />
        )}
        {axleStatus === AxleIframeComponentStatus.EXITED && (
          <AxleExitStatus onStartSession={restartComponentValue} />
        )}
        {modalOpen && (
          <GenericModal
            title="Are you sure you want to restart your Axle session?"
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            onAction={restartConnectedSession}
            textAction="Restart Session"
            textCancel="Cancel"
            hintText="The connected policy data will be lost"
          />
        )}
      </FormControl>
    </SizingContainer>
  )
}
